// src/utils/videoWorkflow.js
//
// Pass 71 — the ONE canonical video read-model. Every mounted surface (instructor queue, student videos,
// feedback editor) MUST derive video assignment + lifecycle from these helpers, never invent its own
// precedence. `videos` (top-level) is the single video truth; this file holds no Firestore access (pure).
//
// FROZEN assignment semantic (Pass 71 §6): VIDEO assignment is an Auth uid. Every writer sets
// `assignedInstructorId` AND `assignedTo` to the coach's Auth uid together; `instructorId` is a very-legacy
// fallback. This is NOT the lesson two-ID model — do not import it here.

const str = (v) => (v == null ? '' : String(v).trim());

export function getVideoTitle(video) {
  return video?.videoName || video?.title || 'Untitled Video';
}

export function getVideoUrl(video) {
  return video?.videoURL || video?.videoUrl || '';
}

// Canonical assignee resolver — the modern `assignedInstructorId`, then legacy `assignedTo`, then very-legacy
// `instructorId`. Returns the Auth uid or '' (never null, so callers can compare with ===).
export function getVideoAssignedInstructorAuthId(video) {
  return str(video?.assignedInstructorId) || str(video?.assignedTo) || str(video?.instructorId) || '';
}

// Back-compat name (was `assignedTo || instructorId`, which ignored the modern field). Now the canonical resolver.
export function getAssignedInstructorId(video) {
  return getVideoAssignedInstructorAuthId(video) || null;
}

// The record disagrees with itself about who is assigned — both modern + legacy assignee fields are populated
// with DIFFERENT uids. Such a record is never confidently assigned and is never claimable (§8 fail-safe).
export function hasVideoAssignmentConflict(video) {
  const a = str(video?.assignedInstructorId);
  const b = str(video?.assignedTo);
  return !!a && !!b && a !== b;
}

export function isRealCoachVideo(video) {
  return str(video?.coachType) === 'real';
}

export function isUploadFailed(video) {
  return video?.uploadStatus === 'failed';
}

// Pass 72 (§12) — a MODERN record (explicit `uploadStatus`) is upload-complete ONLY when it is exactly
// 'complete', so a half-finished submission (`uploading`, even with a Storage URL already present) never counts
// as ready. A LEGACY record (no `uploadStatus`) still counts as complete when it has a usable video URL, so old
// valid videos survive. This gates claimability/reviewability consistently.
export function isUploadComplete(video) {
  const uploadStatus = str(video?.uploadStatus);
  if (uploadStatus) return uploadStatus === 'complete';
  return Boolean(getVideoUrl(video));
}

export function isReviewedVideo(video) {
  return video?.status === 'reviewed';
}

export function isPendingVideo(video) {
  return video?.status === 'pending';
}

// Assigned if ANY assignee field resolves, OR the legacy `assigned` boolean is true (covers the
// `assigned:true` but no-assignee contradiction — treat as assigned so it is never re-claimed).
export function isAssignedVideo(video) {
  return Boolean(getVideoAssignedInstructorAuthId(video)) || video?.assigned === true;
}

// Is this video (confidently) assigned to a specific actor's Auth uid? A conflicting record still surfaces in
// the actor's queue if either field matches (so they see it, flagged), but is not "confidently" theirs.
export function isVideoAssignedToActor(video, actorAuthUserId) {
  const uid = str(actorAuthUserId);
  if (!uid) return false;
  if (hasVideoAssignmentConflict(video)) {
    return str(video?.assignedInstructorId) === uid || str(video?.assignedTo) === uid;
  }
  return getVideoAssignedInstructorAuthId(video) === uid;
}

export function isAvailableToClaim(video) {
  return (
    isPendingVideo(video) &&
    !isAssignedVideo(video) &&
    !hasVideoAssignmentConflict(video) &&
    isRealCoachVideo(video) &&
    isUploadComplete(video)
  );
}

export function isInReviewVideo(video) {
  return isPendingVideo(video) && isAssignedVideo(video) && !isUploadFailed(video);
}

// The single presentation state machine — persisted workflow state (pending|reviewed) + upload state + derived
// assignment produce ONE derived state consumed by both instructor and student surfaces.
export function getVideoWorkflowState(video) {
  if (isUploadFailed(video)) return 'failed';
  if (!isUploadComplete(video)) return 'uploading';
  if (isReviewedVideo(video)) return 'reviewed';
  if (isInReviewVideo(video)) return 'in_review';
  return 'uploaded';
}

// Pass 71A (§20) — the student badge DERIVES from the ONE canonical `getVideoWorkflowState`, so the student and
// instructor surfaces can never disagree. A `key` of `awaiting_instructor` maps the canonical `uploaded` state
// (kept for existing callers); a still-uploading video now reads "Uploading", not "Awaiting instructor".
export function getStudentVideoState(video) {
  const state = getVideoWorkflowState(video);
  if (state === 'failed') {
    return {
      key: 'upload_failed',
      label: 'Upload failed',
      tone: 'text-red-700 bg-red-100',
      description: video?.uploadError || 'This upload did not finish. Re-upload this video to continue.',
    };
  }
  if (state === 'uploading') {
    return {
      key: 'uploading',
      label: 'Uploading',
      tone: 'text-slate-700 bg-slate-100',
      description: 'This upload is still finishing. It will be ready for a coach once complete.',
    };
  }
  if (state === 'reviewed') {
    return {
      key: 'reviewed',
      label: 'Reviewed',
      tone: 'text-green-700 bg-green-100',
      description: 'Your instructor feedback is ready below.',
    };
  }
  if (state === 'in_review') {
    return {
      key: 'in_review',
      label: 'In review',
      tone: 'text-amber-700 bg-amber-100',
      description: 'An instructor has claimed this video and is working on feedback.',
    };
  }
  return {
    key: 'awaiting_instructor',
    label: 'Awaiting instructor',
    tone: 'text-blue-700 bg-blue-100',
    description: 'Your upload is ready and waiting to be claimed.',
  };
}
